/* AdminMgmtFinance — Money group satellite (P0 + P1 + P2 features).
 *
 * 6 tabs that augment the existing AdminFinance page WITHOUT modifying it.
 * Lives at /admin/finance-hub.
 *
 * Tabs:
 *   1. Reconciliation (bank-feed ↔ wallet_txns drift)
 *   2. Dunning (overdue invoice aging + campaigns)
 *   3. Disputes (chargeback / dispute mgmt)
 *   4. Subscriptions (SaaS plan per tenant + MRR)
 *   5. Payouts (scheduled outbound payouts timeline)
 *   6. Tax (1099 candidates + form generation)
 */
import { useEffect, useState, useCallback } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { PageHeader } from "../../components/PortalShell";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../components/ui/dialog";
import {
  Banknote, AlertCircle, Scale, Layers, CalendarDays, FileSpreadsheet, Loader2,
  CheckCircle2, ShieldOff, Plus,
} from "lucide-react";
import {
  MgmtKpi, MgmtSectionHeader, MgmtEmpty, ReasonField, TABS_STYLE, TabBtn,
} from "../../components/AdminMgmtCommon";

const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const TABS = [
  { id: "recon",    label: "Reconciliation", icon: Banknote },
  { id: "dunning",  label: "Dunning",        icon: AlertCircle },
  { id: "disputes", label: "Disputes",       icon: Scale },
  { id: "subs",     label: "Subscriptions",  icon: Layers },
  { id: "payouts",  label: "Payouts",        icon: CalendarDays },
  { id: "tax",      label: "Tax · 1099",     icon: FileSpreadsheet },
];

export default function AdminMgmtFinance() {
  const [tab, setTab] = useState("recon");
  return (
    <div className="p-6 space-y-5 max-w-7xl" data-testid="admin-mgmt-finance">
      <PageHeader title="Finance Hub" subtitle="Reconciliation · dunning · disputes · SaaS subscriptions · payouts · tax forms — admin oversight only."/>
      <div className={TABS_STYLE} data-testid="finhub-tabs">
        {TABS.map(t => (
          <TabBtn key={t.id} active={tab === t.id} onClick={() => setTab(t.id)}
            icon={t.icon} label={t.label} testid={`finhub-tab-${t.id}`}/>
        ))}
      </div>
      {tab === "recon"    && <ReconTab/>}
      {tab === "dunning"  && <DunningTab/>}
      {tab === "disputes" && <DisputesTab/>}
      {tab === "subs"     && <SubscriptionsTab/>}
      {tab === "payouts"  && <PayoutsTab/>}
      {tab === "tax"      && <TaxTab/>}
    </div>
  );
}

/* ────────── 1. Reconciliation ────────── */
function ReconTab() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try { const r = await api.get(`/admin/mgmt/recon/overview?days=${days}`); setData(r.data); }
    catch (e) { toast.error("Failed to load reconciliation"); }
    finally { setBusy(false); }
  }, [days]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="p-10 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-foreground/40"/></div>;
  const t = data.totals;
  return (
    <div className="space-y-4">
      <MgmtSectionHeader
        title="Bank ↔ Ledger reconciliation"
        subtitle={`Window: last ${days} days · ${data.rails.length} rails compared · as of ${new Date(data.as_of).toLocaleTimeString()}`}
        onRefresh={load} busy={busy}
        action={
          <select value={days} onChange={(e) => setDays(Number(e.target.value))}
            className="h-8 border rounded-md px-2 text-[12.5px] bg-background"
            data-testid="recon-window">
            <option value={7}>7 days</option>
            <option value={30}>30 days</option>
            <option value={90}>90 days</option>
          </select>
        }
      />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
        <MgmtKpi label="Internal (ledger)" value={money(t.internal)} tone="ink"/>
        <MgmtKpi label="External (bank feed)" value={money(t.external)}/>
        <MgmtKpi label="Drift" value={money(t.drift)} tone={Math.abs(t.drift) < 1 ? "emerald" : "amber"}/>
        <MgmtKpi label="Open breaks" value={data.open_breaks} tone={data.open_breaks > 0 ? "rose" : "emerald"} testid="recon-breaks-tile"/>
      </div>
      <div className="rounded-xl border overflow-hidden">
        <table className="w-full text-[12.5px]" data-testid="recon-rails-table">
          <thead className="bg-foreground/5 text-left text-[11px] uppercase tracking-wider text-foreground/55">
            <tr>
              <th className="px-3 py-2">Rail</th>
              <th className="px-3 py-2 text-right">Internal</th>
              <th className="px-3 py-2 text-right">External</th>
              <th className="px-3 py-2 text-right">Drift</th>
              <th className="px-3 py-2">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {data.rails.map(r => (
              <tr key={r.rail} data-testid={`recon-row-${r.rail}`}>
                <td className="px-3 py-2 font-medium">{r.rail}</td>
                <td className="px-3 py-2 text-right mono">{money(r.internal_total)}</td>
                <td className="px-3 py-2 text-right mono">{money(r.external_total)}</td>
                <td className={`px-3 py-2 text-right mono ${Math.abs(r.drift) < 0.01 ? "" : r.status === "alert" ? "text-rose-700" : "text-amber-700"}`}>
                  {money(r.drift)}
                </td>
                <td className="px-3 py-2">
                  <span className={`chip text-[10px] ${r.status === "ok" ? "bg-emerald-50 border-emerald-200 text-emerald-700" :
                    r.status === "alert" ? "bg-rose-50 border-rose-200 text-rose-700" : "bg-amber-50 border-amber-200 text-amber-700"}`}>
                    {r.status}
                  </span>
                </td>
              </tr>
            ))}
            {data.rails.length === 0 && <tr><td colSpan={5}><MgmtEmpty message="No transactions in this window."/></td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ────────── 2. Dunning ────────── */
function DunningTab() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try { const r = await api.get("/admin/mgmt/dunning/overview"); setData(r.data); }
    catch (e) { toast.error("Failed to load dunning data"); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="p-10 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-foreground/40"/></div>;
  const buckets = data.buckets || {};
  return (
    <div className="space-y-4">
      <MgmtSectionHeader
        title="Overdue invoice aging"
        subtitle={`${data.total_overdue_count} invoices · ${money(data.total_overdue_amount)} outstanding`}
        onRefresh={load} busy={busy}
        action={<Button size="sm" onClick={() => setCreateOpen(true)} data-testid="dunning-create-btn"><Plus className="w-3 h-3 mr-1"/>Campaign</Button>}
      />
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2.5">
        {Object.entries(buckets).map(([k, v]) => (
          <MgmtKpi key={k} label={`${k} days overdue`} value={v.count} hint={money(v.amount)}
            tone={k === "60+" ? "rose" : k === "31-60" ? "amber" : "neutral"}
            testid={`dunning-bucket-${k}`}/>
        ))}
      </div>
      {data.campaigns?.length > 0 && (
        <div className="rounded-xl border overflow-hidden" data-testid="dunning-campaigns-table">
          <div className="px-3 py-2 border-b text-[12px] font-semibold bg-foreground/5">Active campaigns</div>
          <table className="w-full text-[12.5px]">
            <thead className="bg-foreground/3 text-left text-[10.5px] uppercase tracking-wider text-foreground/55">
              <tr><th className="px-3 py-2">Name</th><th className="px-3 py-2">Bucket</th><th className="px-3 py-2">Sent</th><th className="px-3 py-2">Status</th></tr>
            </thead>
            <tbody className="divide-y">
              {data.campaigns.map(c => (
                <tr key={c.id} data-testid={`dunning-campaign-${c.id}`}>
                  <td className="px-3 py-2 font-medium">{c.name}</td>
                  <td className="px-3 py-2 mono">{c.bucket}</td>
                  <td className="px-3 py-2 mono">{c.stats?.sent || 0}</td>
                  <td className="px-3 py-2">
                    <span className={`chip text-[10px] ${c.enabled ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-foreground/5 border-foreground/10"}`}>
                      {c.enabled ? "live" : "paused"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="rounded-xl border overflow-hidden">
        <div className="px-3 py-2 border-b text-[12px] font-semibold bg-foreground/5">Top 60+ days overdue</div>
        <table className="w-full text-[12.5px]" data-testid="dunning-top-overdue-table">
          <thead className="bg-foreground/3 text-left text-[10.5px] uppercase tracking-wider text-foreground/55">
            <tr><th className="px-3 py-2">Invoice</th><th className="px-3 py-2">Buyer</th><th className="px-3 py-2">Amount</th><th className="px-3 py-2">Days</th></tr>
          </thead>
          <tbody className="divide-y">
            {(buckets["60+"]?.rows || []).slice(0, 10).map(inv => (
              <tr key={inv.id}>
                <td className="px-3 py-2 mono text-[11px]">{inv.code}</td>
                <td className="px-3 py-2">{inv.buyer_name}</td>
                <td className="px-3 py-2 mono">{money(inv.amount)}</td>
                <td className="px-3 py-2 text-rose-700">{inv.days_overdue}d</td>
              </tr>
            ))}
            {(buckets["60+"]?.rows || []).length === 0 && (
              <tr><td colSpan={4}><MgmtEmpty message="No 60+ day delinquencies."/></td></tr>
            )}
          </tbody>
        </table>
      </div>
      <DunningCampaignDialog open={createOpen} onClose={() => setCreateOpen(false)} onSaved={load}/>
    </div>
  );
}

function DunningCampaignDialog({ open, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [bucket, setBucket] = useState("8-14");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setName(""); setBucket("8-14"); setReason(""); } }, [open]);

  const submit = async () => {
    if (name.length < 2 || reason.trim().length < 3) {
      toast.error("Name + reason required"); return;
    }
    setBusy(true);
    try {
      await api.post("/admin/mgmt/dunning/campaigns", { name, bucket, reason: reason.trim() });
      toast.success("Campaign created");
      onSaved(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="dunning-create-dialog">
        <DialogHeader><DialogTitle>New dunning campaign</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div><Label>Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="dunning-name"/></div>
          <div>
            <Label>Bucket</Label>
            <select value={bucket} onChange={(e) => setBucket(e.target.value)}
              className="w-full h-9 border rounded-md px-2 text-sm bg-background" data-testid="dunning-bucket">
              <option value="1-7">1-7 days</option>
              <option value="8-14">8-14 days</option>
              <option value="15-30">15-30 days</option>
              <option value="31-60">31-60 days</option>
              <option value="60+">60+ days</option>
            </select>
          </div>
          <ReasonField value={reason} onChange={setReason}/>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy || reason.trim().length < 3} data-testid="dunning-submit">
            {busy && <Loader2 className="w-3 h-3 mr-1 animate-spin"/>}Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ────────── 3. Disputes ────────── */
function DisputesTab() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [decideRow, setDecideRow] = useState(null);

  const load = useCallback(async () => {
    setBusy(true);
    try { const r = await api.get("/admin/mgmt/disputes/overview"); setData(r.data); }
    catch (e) { toast.error("Failed to load disputes"); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="p-10 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-foreground/40"/></div>;
  return (
    <div className="space-y-4">
      <MgmtSectionHeader title="Disputes & chargebacks"
        subtitle={`${data.count} total · ${money(data.totals.amount)} disputed · ${data.totals.win_rate}% win rate`}
        onRefresh={load} busy={busy}/>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
        <MgmtKpi label="Open" value={data.by_status?.open || 0} tone="amber" testid="disputes-open-tile"/>
        <MgmtKpi label="Won (lifetime)" value={data.totals.won} tone="emerald" hint={money(data.totals.won_amount)}/>
        <MgmtKpi label="Lost (lifetime)" value={data.totals.lost} tone="rose" hint={money(data.totals.lost_amount)}/>
        <MgmtKpi label="Win rate" value={`${data.totals.win_rate}%`}/>
      </div>
      <div className="rounded-xl border overflow-hidden">
        <table className="w-full text-[12.5px]" data-testid="disputes-table">
          <thead className="bg-foreground/5 text-left text-[11px] uppercase tracking-wider text-foreground/55">
            <tr><th className="px-3 py-2">Opened</th><th className="px-3 py-2">Amount</th><th className="px-3 py-2">Rail</th><th className="px-3 py-2">Reason</th><th className="px-3 py-2">Status</th><th className="px-3 py-2"></th></tr>
          </thead>
          <tbody className="divide-y">
            {data.rows.map(r => (
              <tr key={r.id} data-testid={`dispute-row-${r.id}`}>
                <td className="px-3 py-2 mono text-[11px]">{r.opened_at ? new Date(r.opened_at).toLocaleDateString() : "—"}</td>
                <td className="px-3 py-2 mono">{money(r.amount)}</td>
                <td className="px-3 py-2">{r.rail || "—"}</td>
                <td className="px-3 py-2 max-w-xs truncate">{r.reason_code}</td>
                <td className="px-3 py-2">
                  <span className={`chip text-[10px] ${
                    r.status === "won" ? "bg-emerald-50 border-emerald-200 text-emerald-700" :
                    r.status === "lost" ? "bg-rose-50 border-rose-200 text-rose-700" :
                    "bg-amber-50 border-amber-200 text-amber-700"}`}>
                    {r.status}
                  </span>
                </td>
                <td className="px-3 py-2 text-right">
                  {r.status === "open" && (
                    <Button size="sm" variant="ghost" onClick={() => setDecideRow(r)} data-testid={`dispute-decide-${r.id}`}>Decide</Button>
                  )}
                </td>
              </tr>
            ))}
            {data.rows.length === 0 && <tr><td colSpan={6}><MgmtEmpty message="No disputes recorded."/></td></tr>}
          </tbody>
        </table>
      </div>
      <DecideDisputeDialog row={decideRow} onClose={() => setDecideRow(null)} onSaved={load}/>
    </div>
  );
}

function DecideDisputeDialog({ row, onClose, onSaved }) {
  const [status, setStatus] = useState("won");
  const [note, setNote] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (row) { setStatus("won"); setNote(""); setReason(""); } }, [row]);
  if (!row) return null;
  const submit = async () => {
    if (reason.trim().length < 3) { toast.error("Reason ≥ 3 chars"); return; }
    setBusy(true);
    try {
      await api.post(`/admin/mgmt/disputes/${row.id}/decide`, { status, reason: reason.trim(), decision_note: note });
      toast.success(`Dispute ${status}`); onSaved(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!row} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="dispute-decide-dialog">
        <DialogHeader><DialogTitle>Decide dispute</DialogTitle><DialogDescription>{row.reason_code} · ${row.amount?.toFixed(2)}</DialogDescription></DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label>Decision</Label>
            <select value={status} onChange={(e) => setStatus(e.target.value)}
              className="w-full h-9 border rounded-md px-2 text-sm bg-background" data-testid="dispute-status">
              <option value="won">Won (merchant favor)</option>
              <option value="lost">Lost (chargeback stands)</option>
              <option value="withdrawn">Withdrawn</option>
            </select>
          </div>
          <div><Label>Note (internal)</Label><Input value={note} onChange={(e) => setNote(e.target.value)}/></div>
          <ReasonField value={reason} onChange={setReason}/>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy || reason.trim().length < 3} data-testid="dispute-decide-submit">
            {busy && <Loader2 className="w-3 h-3 mr-1 animate-spin"/>}Apply
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ────────── 4. Subscriptions ────────── */
function SubscriptionsTab() {
  const [plans, setPlans] = useState([]);
  const [subs, setSubs] = useState(null);
  const [busy, setBusy] = useState(false);
  const [planFilter, setPlanFilter] = useState("");
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [p, s] = await Promise.all([
        api.get("/admin/mgmt/subscriptions/plans"),
        api.get(`/admin/mgmt/subscriptions?plan=${planFilter}&q=${encodeURIComponent(q)}`),
      ]);
      setPlans(p.data.plans); setSubs(s.data);
    } catch (e) { toast.error("Failed to load subscriptions"); }
    finally { setBusy(false); }
  }, [planFilter, q]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4">
      <MgmtSectionHeader
        title="SaaS subscriptions"
        subtitle={subs ? `${subs.count} active tenants · ${money(subs.mrr_usd)} MRR` : ""}
        onRefresh={load} busy={busy}/>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
        {plans.map(p => (
          <MgmtKpi key={p.id} label={p.name} value={money(p.price_mo) + "/mo"}
            hint={`${p.limits?.users === -1 ? "∞" : p.limits?.users} seats · ${p.limits?.orders_mo === -1 ? "∞" : p.limits?.orders_mo} orders/mo`}/>
        ))}
      </div>
      <div className="flex gap-2 items-end">
        <div className="flex-1"><Label>Search</Label><Input placeholder="email · company" value={q} onChange={(e) => setQ(e.target.value)} data-testid="subs-search"/></div>
        <div className="min-w-[160px]">
          <Label>Plan</Label>
          <select value={planFilter} onChange={(e) => setPlanFilter(e.target.value)}
            className="w-full h-9 border rounded-md px-2 text-sm bg-background" data-testid="subs-plan-filter">
            <option value="">All plans</option>
            {plans.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
      </div>
      <div className="rounded-xl border overflow-hidden">
        <table className="w-full text-[12.5px]" data-testid="subs-table">
          <thead className="bg-foreground/5 text-left text-[11px] uppercase tracking-wider text-foreground/55">
            <tr><th className="px-3 py-2">Tenant</th><th className="px-3 py-2">Plan</th><th className="px-3 py-2">Price/mo</th><th className="px-3 py-2">Started</th><th className="px-3 py-2">Status</th></tr>
          </thead>
          <tbody className="divide-y">
            {(subs?.rows || []).map(r => (
              <tr key={r.user_id} data-testid={`sub-row-${r.user_id}`}>
                <td className="px-3 py-2">{r.company || r.user_email}<div className="text-[10px] text-foreground/55 mono">{r.user_email}</div></td>
                <td className="px-3 py-2 font-medium">{r.plan_id}</td>
                <td className="px-3 py-2 mono">{money(r.price_mo)}</td>
                <td className="px-3 py-2 mono text-[11px]">{r.started_at ? new Date(r.started_at).toLocaleDateString() : "—"}</td>
                <td className="px-3 py-2">
                  <span className={`chip text-[10px] ${r.status === "active" ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-foreground/5"}`}>
                    {r.status}
                  </span>
                </td>
              </tr>
            ))}
            {(!subs?.rows || subs.rows.length === 0) && <tr><td colSpan={5}><MgmtEmpty message="No subscriptions assigned yet."/></td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ────────── 5. Payouts ────────── */
function PayoutsTab() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    setBusy(true);
    try { const r = await api.get("/admin/mgmt/payouts/schedule"); setData(r.data); }
    catch (e) { toast.error("Failed to load payouts"); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="p-10 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-foreground/40"/></div>;
  const byDay = Object.entries(data.by_day || {}).sort();
  return (
    <div className="space-y-4">
      <MgmtSectionHeader title="Pending payouts schedule" subtitle={`${data.rows.length} payouts queued · ${money(data.total_pending)} total`} onRefresh={load} busy={busy}/>
      <div className="rounded-xl border p-4 bg-background" data-testid="payouts-by-day">
        <div className="text-[11.5px] font-semibold mb-2">By scheduled day</div>
        {byDay.length === 0 ? <MgmtEmpty message="No scheduled payouts."/> : (
          <div className="space-y-1.5">
            {byDay.slice(0, 14).map(([d, v]) => (
              <div key={d} className="flex items-center gap-3 text-[12px]">
                <div className="w-24 mono text-foreground/60">{d}</div>
                <div className="flex-1 h-2 bg-foreground/5 rounded-full overflow-hidden">
                  <div className="h-full bg-foreground/80" style={{ width: `${Math.min(100, v.amount / data.total_pending * 100)}%` }}/>
                </div>
                <div className="w-24 text-right mono">{money(v.amount)}</div>
                <div className="w-12 text-right text-foreground/55 mono text-[10.5px]">{v.count}</div>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="rounded-xl border overflow-hidden">
        <table className="w-full text-[12.5px]" data-testid="payouts-table">
          <thead className="bg-foreground/5 text-left text-[11px] uppercase tracking-wider text-foreground/55">
            <tr><th className="px-3 py-2">When</th><th className="px-3 py-2">Payee</th><th className="px-3 py-2">Amount</th><th className="px-3 py-2">Rail</th><th className="px-3 py-2">Status</th></tr>
          </thead>
          <tbody className="divide-y">
            {data.rows.slice(0, 50).map(r => (
              <tr key={r.id} data-testid={`payout-${r.id}`}>
                <td className="px-3 py-2 mono text-[11px]">{(r.scheduled_for || r.created_at || "").slice(0, 10)}</td>
                <td className="px-3 py-2 text-[11px]">{(r.user_id || "").slice(0, 14)}…</td>
                <td className="px-3 py-2 mono">{money(r.amount)}</td>
                <td className="px-3 py-2">{r.rail || "—"}</td>
                <td className="px-3 py-2"><span className="chip text-[10px] bg-amber-50 border-amber-200 text-amber-700">{r.status}</span></td>
              </tr>
            ))}
            {data.rows.length === 0 && <tr><td colSpan={5}><MgmtEmpty/></td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ────────── 6. Tax / 1099 ────────── */
function TaxTab() {
  const [year, setYear] = useState(new Date().getUTCFullYear() - 1);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    setBusy(true);
    try { const r = await api.get(`/admin/mgmt/tax/1099-candidates?year=${year}`); setData(r.data); }
    catch (e) { toast.error("Failed to load 1099 data"); }
    finally { setBusy(false); }
  }, [year]);
  useEffect(() => { load(); }, [load]);

  const generate = async (uid) => {
    const reason = window.prompt(`Generate 1099-NEC for ${year} · user ${uid.slice(0, 8)}…? (reason, min 3 chars)`);
    if (!reason || reason.trim().length < 3) return;
    try {
      await api.post("/admin/mgmt/tax/1099-generate", { user_id: uid, year, reason: reason.trim() });
      toast.success("1099 generated"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (!data) return <div className="p-10 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-foreground/40"/></div>;
  return (
    <div className="space-y-4">
      <MgmtSectionHeader
        title="1099-NEC candidates"
        subtitle={`Year ${year} · ${data.count} payees ≥ $${data.threshold_usd}`}
        onRefresh={load} busy={busy}
        action={
          <select value={year} onChange={(e) => setYear(Number(e.target.value))}
            className="h-8 border rounded-md px-2 text-[12.5px] bg-background" data-testid="tax-year">
            {[0, 1, 2, 3].map(off => {
              const y = new Date().getUTCFullYear() - off; return <option key={y} value={y}>{y}</option>;
            })}
          </select>
        }
      />
      <div className="rounded-xl border overflow-hidden">
        <table className="w-full text-[12.5px]" data-testid="tax-table">
          <thead className="bg-foreground/5 text-left text-[11px] uppercase tracking-wider text-foreground/55">
            <tr><th className="px-3 py-2">Payee</th><th className="px-3 py-2">Email</th><th className="px-3 py-2 text-right">Total paid</th><th className="px-3 py-2">Txns</th><th className="px-3 py-2">Form</th><th className="px-3 py-2"></th></tr>
          </thead>
          <tbody className="divide-y">
            {data.candidates.map(c => (
              <tr key={c.user_id} data-testid={`tax-row-${c.user_id}`}>
                <td className="px-3 py-2 font-medium">{c.name || c.company || "—"}</td>
                <td className="px-3 py-2 mono text-[11px]">{c.user_email}</td>
                <td className="px-3 py-2 text-right mono">{money(c.total_paid)}</td>
                <td className="px-3 py-2 text-foreground/55">{c.txn_count}</td>
                <td className="px-3 py-2">
                  {c.form_generated ? (
                    <span className="chip text-[10px] bg-emerald-50 border-emerald-200 text-emerald-700"><CheckCircle2 className="w-3 h-3 mr-0.5"/>generated</span>
                  ) : <span className="text-foreground/45 text-[11px]">pending</span>}
                </td>
                <td className="px-3 py-2 text-right">
                  {!c.form_generated && (
                    <Button size="sm" variant="ghost" onClick={() => generate(c.user_id)} data-testid={`tax-gen-${c.user_id}`}>Generate</Button>
                  )}
                </td>
              </tr>
            ))}
            {data.candidates.length === 0 && <tr><td colSpan={6}><MgmtEmpty message="No 1099 candidates this year."/></td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
